import { Service, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import type { IPermiso, IToken } from 'modelos';
import { AutenticacionApi } from './autenticacion.api';
import { SesionStore } from './sesion.store';

/**
 * ¿El servidor **rechazó** el refresh token, o simplemente no pudo contestar?
 *
 * Sólo un rechazo explícito (400 `invalid_grant`, 401, 403) significa que la
 * sesión está muerta. Un 503 (la api de autenticación o Redis caídos), un 504 o
 * un error de red no dicen nada del refresh token: mandar al login ahí expulsa a
 * todos los usuarios activos por un parpadeo de infraestructura.
 *
 * Es la misma clasificación que hacen IRIX y el móvil, y es la razón por la que
 * `HttpClientService` se lee con cuidado antes de tocarlo.
 */
export function refrescoRechazado(error: unknown): boolean {
  const estado = (error as { status?: number } | null)?.status;
  return estado === 400 || estado === 401 || estado === 403;
}

/**
 * Todo lo que cambia la sesión: entrar, salir, refrescar y cambiar de permiso.
 *
 * El estado vive en `SesionStore`; acá está el orden de las llamadas.
 */
@Service()
export class SesionService {
  private readonly api = inject(AutenticacionApi);
  private readonly store = inject(SesionStore);
  private readonly router = inject(Router);

  /**
   * Refresco en vuelo, compartido.
   *
   * Si diez requests reciben 401 a la vez, se refresca **una** sola vez y las
   * diez esperan la misma promesa.
   */
  private refrescoEnVuelo?: Promise<IToken>;

  /** Se emite cada vez que cambia el permiso activo: sirve para vaciar caches. */
  readonly permisoCambiado = signal(0);

  // ── Entrar ─────────────────────────────────────────────────────────────────

  async login(usuario: string, clave: string): Promise<void> {
    const token = await this.api.login(usuario, clave);
    await this.procesarToken(token);
  }

  async loginGoogle(idToken: string): Promise<void> {
    const token = await this.api.loginGoogle(idToken);
    await this.procesarToken(token);
  }

  private async procesarToken(token: IToken): Promise<void> {
    this.store.guardarTokens(token);
    const acceso = token.accessToken ?? '';

    const [usuario, permisos] = await Promise.all([
      this.api.usuarioPropio(acceso),
      this.api.permisosMios(acceso),
    ]);

    this.store.setUsuario(usuario);
    this.store.setPermisos(permisos);
    await this.seleccionarPermisoInicial(acceso, permisos);
  }

  /**
   * Autoselecciona el permiso: el último usado si sigue activo, si no el primero
   * activo. Sin permisos activos no se setea contexto y la app manda a la
   * pantalla de "sin acceso".
   */
  private async seleccionarPermisoInicial(acceso: string, permisos: IPermiso[]): Promise<void> {
    const activos = (permisos ?? []).filter((p) => !!p.activo);
    if (!activos.length) {
      this.store.setPermisoActual(undefined);
      return;
    }

    const anterior = this.store.idPermiso();
    const elegido = activos.find((p) => p._id === anterior) ?? activos[0];
    this.store.setPermisoActual(elegido);

    const cliente = await this.api.clientePropio(acceso, elegido._id);
    this.store.setClientePropio(cliente);
    this.store.setCliente(cliente);
  }

  // ── Cambiar de permiso ─────────────────────────────────────────────────────

  /**
   * Un operador de una empresa que presta servicio a varios municipios cambia de
   * contexto por acá. Todo lo cacheado tiene que invalidarse: por eso se emite
   * `permisoCambiado`.
   *
   * El cliente del permiso nuevo se pide **antes** de fijar nada: si el pedido
   * falla, la sesión sigue entera en el permiso anterior y el error le llega a
   * quien llamó. Fijando primero el permiso quedaba un `x-permiso` nuevo con el
   * tema, `usaPuestas` y el nivel del cliente viejo.
   */
  async cambiarPermiso(idPermiso: string): Promise<void> {
    const permiso = this.store.permisos().find((p) => p._id === idPermiso);
    if (!permiso) return;

    const cliente = await this.api.clientePropio(this.store.accessToken(), idPermiso);

    this.store.setPermisoActual(permiso);
    this.store.setClientePropio(cliente);
    this.store.setCliente(cliente);

    this.permisoCambiado.update((n) => n + 1);
  }

  // ── Refrescar ──────────────────────────────────────────────────────────────

  /**
   * Devuelve la promesa del refresco en curso, o arranca uno.
   *
   * Si el servidor rechaza el refresh se cierra la sesión y se va al login. Si
   * el servidor no pudo contestar, el error se propaga y la sesión queda
   * intacta: el request original falla, pero el usuario no pierde la sesión.
   *
   * Si el token se renovó pero el **permiso** activo ya no sirve (un 403 de
   * `/clientes/propio`: vencido, deshabilitado, cliente dado de baja), la
   * sesión es válida: se va a `/sin-acceso`, que ofrece cambiar de permiso, en
   * vez de echar al usuario al login.
   */
  refrescar(): Promise<IToken> {
    this.refrescoEnVuelo ??= this.hacerRefresco().finally(() => {
      this.refrescoEnVuelo = undefined;
    });
    return this.refrescoEnVuelo;
  }

  private async hacerRefresco(): Promise<IToken> {
    const refresh = this.store.refreshToken();
    if (!refresh) {
      this.cerrarSesionPorTokenInvalido();
      throw new Error('No hay refresh token');
    }

    let token: IToken;
    try {
      token = await this.api.refrescar(refresh);
    } catch (error) {
      if (refrescoRechazado(error)) this.cerrarSesionPorTokenInvalido();
      throw error;
    }
    this.store.guardarTokens(token);

    try {
      const acceso = token.accessToken ?? '';

      // Los permisos pueden haber cambiado sin que el usuario haya vuelto a
      // entrar (alta o baja hecha por un administrador mientras trabajaba).
      const [usuario, permisos] = await Promise.all([
        this.api.usuarioPropio(acceso),
        this.api.permisosMios(acceso),
      ]);
      this.store.setUsuario(usuario);
      this.store.setPermisos(permisos);

      const idPermiso = this.store.idPermiso();
      if (idPermiso) {
        const cliente = await this.api.clientePropio(acceso, idPermiso);
        this.store.setClientePropio(cliente);
        // `cliente` es el que el operador eligió en el selector y puede no ser
        // el propio: sólo se pisa si todavía no había elegido ninguno.
        if (!this.store.cliente()) this.store.setCliente(cliente);
      }

      return token;
    } catch (error) {
      // Con el token recién renovado, un 403 es del permiso, no de la sesión.
      if ((error as { status?: number } | null)?.status === 403) {
        void this.router.navigate(['/sin-acceso']);
      } else if (refrescoRechazado(error)) {
        this.cerrarSesionPorTokenInvalido();
      }
      throw error;
    }
  }

  private cerrarSesionPorTokenInvalido(): void {
    this.store.limpiar();
    void this.router.navigate(['/login']);
  }

  // ── Salir ──────────────────────────────────────────────────────────────────

  salir(): void {
    this.store.limpiar();
    void this.router.navigate(['/login']);
  }
}
